import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("exit_shown")) return;
    let armed = false;
    const t = setTimeout(() => (armed = true), 4000);
    const handler = (e) => {
      if (armed && e.clientY <= 0 && !sessionStorage.getItem("exit_shown")) {
        setOpen(true);
        sessionStorage.setItem("exit_shown", "1");
      }
    };
    document.addEventListener("mouseleave", handler);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseleave", handler);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.subscribe(email, "exit_intent");
      toast.success(r.already ? "Already subscribed" : "You're on the list!", {
        description: "Your first weekly digest lands soon — 100% on autopilot.",
      });
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="exit-intent-popup"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/80 p-4 backdrop-blur-md"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white p-10 text-slate-900"
          >
            <button
              data-testid="exit-popup-close"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 text-slate-400 transition-colors duration-200 hover:text-slate-900"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="mb-5 flex h-12 w-12 items-center justify-center bg-emerald-500 text-slate-900">
              <Gift className="h-6 w-6" />
            </div>
            <h2 className="font-display text-3xl font-extrabold leading-none tracking-tighter">
              Wait — grab 15% off
              <br />
              your first template.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Join the autopilot list. Every week we send the highest-rated templates,
              hand-curated by our engine. No spam, unsubscribe anytime.
            </p>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
              <input
                data-testid="exit-popup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition-colors duration-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
              />
              <button
                data-testid="exit-popup-submit"
                disabled={loading}
                className="bg-slate-900 px-6 py-3 font-semibold text-white transition-[background-color] duration-200 hover:bg-emerald-500 hover:text-slate-900 disabled:opacity-60"
              >
                {loading ? "Joining..." : "Unlock my discount"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
